import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Branch from "@NA/Icons/Branch";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";
import toggleLoginDrawer from "@NA/Handlers/ADILoginDrawerHandler";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: CustomApplicationState) => ({
    session: state.context.session,
    userRoles: state.ADIGlobalData.ADIUserRoleStore,
});

const mapDispatchToProps = {
    toggleLoginDrawer,
};

export interface BranchLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    branchText?: TypographyPresentationProps;
}

export const branchLinkStyles: BranchLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 25,
            },
        },
        color: "secondary",
        css: css`
            margin: 0px;
            padding: 0px;
            position: relative;
            display: block !important;
            ${IconWrapper} {
                margin-right: 0px;
                margin-bottom: 3px;
            }
            ${TypographyStyle} {
                color: #002856;
                font-size: 10px;
                font-weight: 400;
                line-height: normal;
                text-transform: capitalize;
                white-space: nowrap;
                display: block;
            }
        `,
    },

    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
    mobileCountText: {
        css: css`
            background: ${getColor("primary")};
            width: 15px;
            height: 15px;
            display: block;
            border-radius: 11px;
            color: ${getColor("primary.contrast")};
            text-align: center;
            font-size: 10px;
            position: absolute;
            left: 18px;
            top: -8px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            right: 2px;
                        `,
                        css`
                            right: 2px;
                        `,
                        css`
                            right: 2px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    branchText: {
        css: css`
            /* font-family: "sans-serif"; */
            font-size: 0.875rem;
            text-transform: capitalize;
            color: ${getColor("secondary")};
        `,
    },
};

const iconMap = {
    branch: Branch,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const styles = branchLinkStyles;
const ADIBranchLink: FC<Props> = ({
    session,
    history,
    fields: { visibilityState, icon: selectedIcon },
    toggleLoginDrawer,
    userRoles
}) => {
    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const showIcon = visibilityState !== "both";
    const showLabel = visibilityState !== "both";

    const icon = iconMap[(showIcon && selectedIcon) || "none"] as IconPresentationProps["src"];

    const { routerLink } = styles;
    const branchUrl = "/myaccount/shopmybranch";
    const redirectToURL = () => {
        if (isAuthenticated) {
            history.push(`${branchUrl}`);
            toggleLoginDrawer({ loginDrawer: false });
        } else {
            toggleLoginDrawer({ loginDrawer: true });
        }
    };

    return (
        <>
            <Link
                {...routerLink}
                onClick={() => redirectToURL()}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="branchLink"
            >
                <Hidden below="lg" as="span">
                    {isAuthenticated && showLabel ? `${translate("My Branch")} ` : `${translate("")} `}
                </Hidden>
            </Link>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ADIBranchLink)),
    definition: {
        displayName: "Branch",
        icon: "branch",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Branch Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },

                    {
                        displayName: "Branch",
                        value: "branch",
                    },
                ],
                defaultValue: "branch",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
